import type {BranchPickerBranch} from '@github-ui/item-picker-branch/BranchPicker'
import type {SetFieldsOperation} from '@github-ui/optimistic-connection'
import {useEntityMutation} from '@github-ui/optimistic-connection/useEntityMutation'
import type {LinkBranchesMutationMutation} from '@github-ui/server-persisted-queries/types/linkBranchesMutation'
import {reactFetchJSON} from '@github-ui/verified-fetch'
import {useCallback} from 'react'

type LinkBranchesMutationParams = {
  issueId: string
  owner: string
  repo: string
  issueNumber: number
  onError: (error: Error) => void
}

type OptimisticLinkedBranches = {
  nodes: Array<{id: string; ref: {id: string}}>
}

function buildOptimisticOperation(issueId: string, linkingBranches: BranchPickerBranch[]): SetFieldsOperation {
  return {
    type: 'setFields',
    id: issueId,
    fields: {
      linkedBranches: {
        nodes: linkingBranches.map(branch => ({
          id: `optimistic-linked-branch-${branch.id}`,
          ref: {
            __typename: 'Ref' as const,
            id: branch.id,
            name: branch.name,
            target: branch.target
              ? {
                  oid: branch.target.oid,
                  id: branch.target.id,
                  __typename: 'Commit',
                }
              : null,
            repository: {
              id: branch.repository.id,
              nameWithOwner: branch.repository.nameWithOwner ?? '',
              defaultBranchRef: branch.repository.defaultBranchRef
                ? {
                    name: branch.repository.defaultBranchRef.name,
                    id: branch.repository.defaultBranchRef.id,
                    target: branch.repository.defaultBranchRef.target
                      ? {
                          oid: branch.repository.defaultBranchRef.target.oid,
                          id: branch.repository.defaultBranchRef.target.id,
                          __typename: 'Commit',
                        }
                      : null,
                    associatedPullRequests: {
                      totalCount: branch.repository.defaultBranchRef.associatedPullRequests.totalCount,
                    },
                  }
                : null,
            },
            associatedPullRequests: {
              totalCount: branch.associatedPullRequests.totalCount,
            },
          },
        })),
      },
    },
  }
}

function linkingIdsFromOperation(operation: SetFieldsOperation): string[] {
  const linkedBranches = operation.fields.linkedBranches as OptimisticLinkedBranches
  return linkedBranches.nodes.map(node => node.ref.id)
}

async function linkBranchesRequest({
  owner,
  repo,
  issueNumber,
  linkingIds,
}: Omit<LinkBranchesMutationParams, 'issueId' | 'onError'> & {linkingIds: string[]}) {
  const response = await reactFetchJSON(
    `/${encodeURIComponent(owner)}/${encodeURIComponent(repo)}/issues/${issueNumber}/linked_branches.json`,
    {
      method: 'PUT',
      body: {linkingIds},
    },
  )

  let result:
    | {data?: LinkBranchesMutationMutation | null; errors?: ReadonlyArray<{message?: string}> | null}
    | undefined
  try {
    result = (await response.json()) as typeof result
  } catch {
    result = undefined
  }

  const error = result?.errors?.find(item => item.message)?.message
  const data = result?.data
  const issue = data?.linkBranches?.issue
  if (!response.ok || error || !issue) throw new Error(error ?? 'Failed to link branches')
  return data
}

export function useLinkBranchesMutation({issueId, owner, repo, issueNumber, onError}: LinkBranchesMutationParams) {
  const {mutate} = useEntityMutation<LinkBranchesMutationMutation>({
    scope: {id: `link-branches:${issueId}`},
    mutationFn: operation =>
      linkBranchesRequest({owner, repo, issueNumber, linkingIds: linkingIdsFromOperation(operation)}),
    getConfirmedOperation: data => {
      const issue = data.linkBranches?.issue
      if (!issue) return undefined
      return {type: 'setFields', id: issue.id, fields: {linkedBranches: issue.linkedBranches}}
    },
    onError,
  })

  return useCallback(
    (linkingBranches: BranchPickerBranch[]) => mutate({operation: buildOptimisticOperation(issueId, linkingBranches)}),
    [issueId, mutate],
  )
}
